import React, { useEffect, useState, useRef, useCallback } from "react";
import { Container, Card, Form, Row, Col, Button, Badge, Alert, Spinner, InputGroup } from "react-bootstrap";
import { api, getStaticData, httpPost, isValidDate, parseEMailPhone } from "../pureFunctions";
import { header, host } from "../globalVariables";

import { useConfig } from "../ConfigContext";

import DatePicker, { registerLocale, setDefaultLocale } from "react-datepicker";
import { de } from "date-fns/locale/de";
registerLocale("de", de);

import {
   generateUebergabeprotokollPDF,
   openPdfInNewTab,
   downloadPdf,
   pdfBytesToBlob,
   pdfBytesToBase64,
} from "./pdfProtocol";
import { sendMail } from "../networkFunctions";
import { buildHandoverEmailHtml, buildHandoverEmailSubject } from "./emailTemplate";
import { useM365Context } from "../m365Context";
import { saveFormState, loadFormState } from "./localFormCache";

// ---------------------------------------------------------------------------
// Konfiguration
// ---------------------------------------------------------------------------
const API_BASE_URL = "/api";
const PDF_ACCENT_COLOR = "#95c11f";
const PDF_LOGO_URL = null;

const ZAEHLER_ARTEN = ["Strom", "Gas", "Wasser (kalt)", "Wasser (warm)", "Fernwärme", "Heizung", "Sonstige"];
const initalBeteiligte = { name: "", email: "", id: "" };

function uid() {
   return Math.random().toString(36).slice(2, 10);
}

// ---------------------------------------------------------------------------
// Unterkomponente: einfaches Unterschriften-Feld (Canvas, Finger/Pencil/Maus)
// ---------------------------------------------------------------------------
const SIGNATURE_WIDTH = 700;
const SIGNATURE_HEIGHT = 180;
// Interne Zeichenfläche an die Gerätepixeldichte koppeln (z. B. Retina-iPad), damit Linien nicht
// verpixelt aussehen - Deckel bei 2x, damit die exportierte PNG-Größe nicht unnötig aufgebläht wird.
const SIGNATURE_DPR = Math.min(typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1, 2);

function SignaturePad({ label, value, onChange }) {
   const canvasRef = useRef(null);
   const drawingRef = useRef(false);

   // Zeichnet eine wiederhergestellte Unterschrift (z. B. aus dem lokalen Zwischenspeicher) auf das
   // Canvas - sonst ist der State zwar gefüllt (und landet im PDF), das Canvas bleibt aber leer.
   useEffect(() => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      if (!value) {
         ctx.clearRect(0, 0, canvas.width, canvas.height);
         return;
      }
      const img = new Image();
      img.onload = () => {
         ctx.clearRect(0, 0, canvas.width, canvas.height);
         ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      };
      img.src = value;
   }, [value]);

   const getCoords = (e) => {
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const point = e.touches ? e.touches[0] : e;
      // Canvas wird per CSS responsiv skaliert (width: 100%) – Koordinaten müssen daher
      // vom angezeigten CSS-Pixelraster auf das interne Canvas-Koordinatensystem umgerechnet werden.
      return {
         x: ((point.clientX - rect.left) * canvas.width) / rect.width,
         y: ((point.clientY - rect.top) * canvas.height) / rect.height,
      };
   };

   const start = (e) => {
      drawingRef.current = true;
      const ctx = canvasRef.current.getContext("2d");
      const { x, y } = getCoords(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
   };

   const move = (e) => {
      if (!drawingRef.current) return;
      e.preventDefault();
      const ctx = canvasRef.current.getContext("2d");
      const { x, y } = getCoords(e);
      ctx.lineWidth = 2 * SIGNATURE_DPR;
      ctx.lineCap = "round";
      ctx.strokeStyle = "#1c1c1c";
      ctx.lineTo(x, y);
      ctx.stroke();
   };

   const end = () => {
      if (!drawingRef.current) return;
      drawingRef.current = false;
      onChange(canvasRef.current.toDataURL("image/png"));
   };

   const clear = () => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      onChange(null);
   };

   return (
      <div className="mb-4">
         <div className="text-uppercase fw-semibold small text-warning-emphasis mb-2">{label}</div>
         <Card className="p-2">
            <div className="d-flex justify-content-end mb-2">
               <Button variant="outline-secondary" size="sm" onClick={clear}>
                  Löschen
               </Button>
            </div>
            <canvas
               ref={canvasRef}
               width={SIGNATURE_WIDTH * SIGNATURE_DPR}
               height={SIGNATURE_HEIGHT * SIGNATURE_DPR}
               style={{
                  width: "100%",
                  maxWidth: SIGNATURE_WIDTH,
                  height: "auto",
                  border: "1px solid #dee2e6",
                  borderRadius: 4,
                  touchAction: "none",
               }}
               onMouseDown={start}
               onMouseMove={move}
               onMouseUp={end}
               onMouseLeave={end}
               onTouchStart={start}
               onTouchMove={move}
               onTouchEnd={end}
            />
            <div className="text-muted small mt-1">Hier unterschreiben</div>
         </Card>
      </div>
   );
}

// ---------------------------------------------------------------------------
// Hauptkomponente
// ---------------------------------------------------------------------------
export default function UebergabeFormular() {
   const config = useConfig();
   const [estateId, setEstateId] = useState(null);
   const [eskStatus, setEskStatus] = useState();
   const [jsonObject, setJsonObject] = useState();
   const [usersData, setUsersData] = useState();

   const [loading, setLoading] = useState(false);
   const [loadError, setLoadError] = useState(null);
   const [submitting, setSubmitting] = useState(false);
   const [spFolderId, setSpFolderId] = useState("");
   const { getSharepointData, getSharepointFile, uploadFileToSharepoint } = useM365Context();

   // 1) Objektdaten
   const [objekt, setObjekt] = useState({
      strasse: "",
      hausnummer: "",
      plz: "",
      ort: "",
      uebergabeDatum: "",
   });

   // 2) Beteiligte
   const [beteiligte, setBeteiligte] = useState({
      owner: [initalBeteiligte],
      buyer: [initalBeteiligte],
      makler: [{ name: "Baur Immobilien GmbH", email: "info@baur-immobilien.de" }],
   });

   // 3) Zählerstände
   const [zaehler, setZaehler] = useState([
      { id: uid(), art: "Strom", nummer: "", stand: "", foto: null },
      { id: uid(), art: "Wasser (kalt)", nummer: "", stand: "", foto: null },
   ]);

   // 4) Schlüssel & Übergabegegenstände
   const [schluessel, setSchluessel] = useState([
      { id: uid(), bezeichnung: "Haustürschlüssel", anzahl: 1 },
      { id: uid(), bezeichnung: "Wohnungsschlüssel", anzahl: 1 },
      { id: uid(), bezeichnung: "Briefkastenschlüssel", anzahl: 1 },
   ]);

   // 5) Zustand, Mängel & Notizen
   const [maengel, setMaengel] = useState("");
   const [notizen, setNotizen] = useState("");

   // 6) Fotos (allgemein)
   const [fotos, setFotos] = useState([]);

   // 7) Unterschriften - ein Eintrag je Person, Index passend zu beteiligte.owner/buyer/makler
   const [signatures, setSignatures] = useState({
      owner: [],
      buyer: [],
      makler: [],
   });

   const updateSignature = (rolle, index, value) => {
      setSignatures((prev) => {
         const list = [...(prev[rolle] || [])];
         list[index] = value;
         return { ...prev, [rolle]: list };
      });
   };

   // -------------------------------------------------------------------------
   // Objektdaten anhand estateId von der API laden
   // -------------------------------------------------------------------------

   async function loadData() {
      const params = new URLSearchParams(window.location.search);
      let url_estateId = params.get("estateId");
      setEstateId(url_estateId);

      if (!url_estateId) return;

      setLoading(true);
      setLoadError(null);

      let [newestatekacheln, cleanUserData, newfieldConfiguration] = await getStaticData();

      setUsersData(cleanUserData);

      let estateBasicData = await api
         .post(
            "/api/getEstateBasicData",
            [
               {
                  id: url_estateId,
                  datarequest: config.uebergabeFormular.estateOOFields,
               },
            ],
            header,
         )
         .catch((err) => {
            console.log(err);

            setLoadError({
               error: "onOffice",
               message:
                  "<strong>Fehler beim Laden der onOffice-Objektdaten</strong> <br/> <br/>" +
                  JSON.stringify(err.response.data, null, 1),
            });
         })
         .finally(() => setLoading(false));
      console.log(estateBasicData);

      let ooJSONObject;
      try {
         ooJSONObject = JSON.parse(estateBasicData.data[config.estate.JSONFeldname] || {});
         setJsonObject(ooJSONObject);
         delete estateBasicData.data[config.estate.JSONFeldname];
      } catch (error) {
         setLoadError({
            error: "onOffice",
            message: "<h2>Fehler im JSON Object</h2>" + estateBasicData.data[config.estate.JSONFeldname],
         });
      }
      try {
         setEskStatus(JSON.parse(estateBasicData.data[config.estate.status] || {}));
         delete estateBasicData.data[config.estate.status];
      } catch (error) {
         setLoadError({
            error: "onOffice",
            message: "<h2>Fehler im JSON Object</h2>" + estateBasicData.data[config.estate.status],
         });
      }
      setSpFolderId(estateBasicData?.data[config.estate.sharepointFolderId]);
      let oldUbergabeData;
      try {
         oldUbergabeData = JSON.parse(estateBasicData.data[config.estateSonstige.uebergabeFormular] || null);
         delete estateBasicData.data[config.estateSonstige.uebergabeFormular];
      } catch (error) {
         setLoadError({
            error: "onOffice",
            message: "<h2>Fehler im JSON Object</h2>" + estateBasicData.data[config.estateSonstige.uebergabeFormular],
         });
      }

      console.log(estateBasicData);

      if (oldUbergabeData) {
         setObjekt(oldUbergabeData.objekt);
         setBeteiligte(oldUbergabeData.beteiligte);
      } else {
         setObjekt((prev) => ({ ...prev, ...estateBasicData.data, ...ooJSONObject }));

         const respEstateBuyer = await loadRelationData(
            url_estateId,
            "buyer",
            config.uebergabeFormular.addressOOFields,
         );
         const respEstateOwner = await loadRelationData(
            url_estateId,
            "owner",
            config.uebergabeFormular.addressOOFields,
         );
         setBeteiligte((prev) => ({
            ...prev,
            makler: [
               {
                  name:
                     cleanUserData?.userObject[estateBasicData.data.benutzer].Nachname +
                     " " +
                     cleanUserData?.userObject[estateBasicData.data.benutzer].Vorname,
                  email: "info@baur-immobilien.de",
               },
            ],
         }));
      }
   }
   useEffect(() => {
      loadData();
   }, []);

   // -------------------------------------------------------------------------
   // Lokaler Zwischenspeicher (IndexedDB): Formularfelder + Fotos überleben einen Reload.
   // -------------------------------------------------------------------------
   const restoredRef = useRef(false);

   // estateId ist beim ersten Aufruf noch null und wird erst asynchron aus der URL gesetzt (loadData) -
   // der Effect läuft daher erneut, sobald sie bekannt ist, und lädt dann unter dem richtigen Key.
   useEffect(() => {
      restoredRef.current = false;
      loadFormState(estateId)
         .then((saved) => {
            if (!saved) return;
            setObjekt((prev) => saved.objekt ?? prev);
            setBeteiligte((prev) => saved.beteiligte ?? prev);
            setZaehler((prev) => saved.zaehler ?? prev);
            setSchluessel((prev) => saved.schluessel ?? prev);
            setMaengel(saved.maengel ?? "");
            setNotizen(saved.notizen ?? "");
            setFotos(saved.fotos ?? []);
            setSignatures((prev) => saved.signatures ?? prev);
         })
         .catch((err) => console.warn("Lokaler Zwischenspeicher konnte nicht gelesen werden:", err))
         .finally(() => {
            restoredRef.current = true;
         });
   }, [estateId]);

   useEffect(() => {
      if (!restoredRef.current) return;
      const timer = setTimeout(() => {
         saveFormState(
            { objekt, beteiligte, zaehler, schluessel, maengel, notizen, fotos, signatures },
            estateId,
         ).catch((err) => console.warn("Formular konnte nicht lokal zwischengespeichert werden:", err));
      }, 600);
      return () => clearTimeout(timer);
   }, [estateId, objekt, beteiligte, zaehler, schluessel, maengel, notizen, fotos, signatures]);

   async function saveData(isDone) {
      let updatedEskStatus;
      if (isDone) {
         updatedEskStatus = structuredClone(eskStatus);
         updatedEskStatus["kachel_status_" + "kaufuebergabe"] = 4;
         updatedEskStatus["kachelabschluss_von_" + "kaufuebergabe"] = usersData.userObject[objekt.benutzer].Name;
      }
      await api
         .post(
            "/api/updateEstateDatenblatt",
            [
               {
                  estateId,
                  oOObjectValue: {
                     [config.estateSonstige.uebergabeFormular]: JSON.stringify({ objekt, beteiligte }, null, 1),
                     ...(isDone
                        ? {
                             [config.estate.status]: JSON.stringify(updatedEskStatus, null, 1),
                             [config.estate.JSONFeldname]: JSON.stringify(
                                {
                                   ...jsonObject,
                                   kaueg_pers: objekt.benutzer,
                                   kaueg_Dat: objekt.uebergabeDatum,
                                },
                                null,
                                1,
                             ),
                          }
                        : {}),
                  },
               },
            ],
            header,
         )
         .then((data) => {
            console.log(data);
         });
   }

   async function loadRelationData(estateId, relationType, fieldList) {
      const respRelationIds = (
         await httpPost(
            host + "/api/EstateRelation",
            { estateId: estateId, relationType: relationType },
            setLoading,
            setLoadError,
         )
      )?.data?.[estateId];

      console.log(respRelationIds);

      const relationData = await Promise.all(
         respRelationIds.map(async (element, index) => {
            const respRelationData =
               (
                  await httpPost(
                     host + "/api/postSpecificAddress",
                     {
                        id: element,
                        data: ["KdNr", "Vorname", "Name", "email", "ID"],
                     },
                     setLoading,
                     setLoadError,
                  )
               )?.data ?? {};

            const emails = parseEMailPhone(respRelationData, "email");
            return {
               email: emails.all[0],
               id: respRelationData.ID,
               name: respRelationData.Name + " " + respRelationData.Vorname,
            };
         }),
      );
      console.log(relationData);

      setBeteiligte((prev) => ({ ...prev, [relationType]: relationData }));
   }

   // -------------------------------------------------------------------------
   // Handler
   // -------------------------------------------------------------------------
   const updateObjekt = (field, value) => setObjekt((prev) => ({ ...prev, [field]: value }));

   function addBeteiligte(rolle) {
      let oldBeteiligte = structuredClone(beteiligte);
      oldBeteiligte[rolle].push({ ...initalBeteiligte });
      setBeteiligte(oldBeteiligte);
   }
   function removeBeteiligte(rolle, index) {
      let oldBeteiligte = structuredClone(beteiligte);
      oldBeteiligte[rolle].splice(index, 1);
      setBeteiligte(oldBeteiligte);
   }

   const updateBeteiligte = (rolle, field, value, index) => {
      let oldBeteiligte = structuredClone(beteiligte);
      if (oldBeteiligte[rolle][index]) {
         oldBeteiligte[rolle][index][field] = value;
      } else {
         oldBeteiligte[rolle].push({ ...initalBeteiligte, [field]: value });
      }
      setBeteiligte(oldBeteiligte);
      /* setBeteiligte((prev) => ({
         ...prev,
         [rolle]: { ...prev[rolle], [field]: value },
      })); */
   };

   const addZaehler = () =>
      setZaehler((prev) => [...prev, { id: uid(), art: ZAEHLER_ARTEN[0], nummer: "", stand: "", foto: null }]);

   const updateZaehler = (id, field, value) =>
      setZaehler((prev) => prev.map((z) => (z.id === id ? { ...z, [field]: value } : z)));

   const removeZaehler = (id) => setZaehler((prev) => prev.filter((z) => z.id !== id));

   const addSchluessel = () => setSchluessel((prev) => [...prev, { id: uid(), bezeichnung: "", anzahl: 1 }]);

   const updateSchluessel = (id, field, value) =>
      setSchluessel((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)));

   const removeSchluessel = (id) => setSchluessel((prev) => prev.filter((s) => s.id !== id));

   const handleZaehlerFoto = (id, file) => {
      if (!file) return;
      updateZaehler(id, "foto", file);
   };

   const handleFotosChange = (fileList) => {
      setFotos((prev) => [...prev, ...Array.from(fileList)]);
   };

   const buildProtocolData = useCallback(
      () => ({
         objekt,
         beteiligte,
         // "Sonstige" wird für die PDF durch den frei eingegebenen Text ersetzt.
         zaehler: zaehler.map((z) => ({
            ...z,
            art: z.art === "Sonstige" ? z.artSonstige?.trim() || "Sonstige" : z.art,
         })),
         schluessel,
         maengel,
         notizen,
         fotos,
         signatures,
      }),
      [objekt, beteiligte, zaehler, schluessel, maengel, notizen, fotos, signatures],
   );

   async function handleSubmit(action, color, logo_url) {
      setSubmitting(true);
      try {
         const pdfBytes = await generateUebergabeprotokollPDF(
            buildProtocolData(),
            {
               accentColor: color,
               logoUrl: logo_url,
            },
            config.uebergabeFormular.estateForm,
         );
         const filename = `Uebergabeformular_${objekt.strasse}_${objekt.hausnummer}_${objekt.ort}.pdf`.replace(
            /\s+/g,
            "_",
         );

         if (action === "preview") {
            openPdfInNewTab(pdfBytes);
            return;
         }
         if (action === "download") {
            downloadPdf(pdfBytes, filename);
         }

         if (action === "download") {
            return;
         }

         // Das PDF wird clientseitig erzeugt; das Backend übernimmt nur noch den Mailversand.

         const propertyLabel = [
            `${objekt.strasse} ${objekt.hausnummer}`.trim(),
            [objekt.plz, objekt.ort].filter(Boolean).join(" "),
         ]
            .filter(Boolean)
            .join(", ");
         const mailSubject = buildHandoverEmailSubject(propertyLabel);
         const mailContent = buildHandoverEmailHtml({ propertyLabel, filename, accentColor: PDF_ACCENT_COLOR });
         const receiver = [...beteiligte.owner, ...beteiligte.buyer];
         await sendMail(
            estateId,
            mailSubject,
            mailContent,
            receiver,
            (resp) => {
               console.log(resp);
            },
            { name: filename, content: pdfBytesToBase64(pdfBytes) },
         );
         await saveData(true);

         //  const blobResponse = await downloadAsPDF(docCanvas);

         const finalBlob = new Blob([pdfBytes], { type: "application/pdf" });
         const uploadResult = await uploadFileToSharepoint(
            finalBlob,
            config.ms365.driveId,
            spFolderId,
            `Uebergabeprotokoll.pdf`,
         );

         if (action === "send") {
            downloadPdf(pdfBytes, filename);
         }

         alert("PDF wurde erstellt und versendet.");
      } catch (error) {
         console.log(error);
         try {
            if (action === "send") {
               downloadPdf(pdfBytes, filename);
            }
         } catch (error2) {
            console.log(error2);
         }

         // alert(`Fehler: ${error.message}`);
      } finally {
         setSubmitting(false);
      }
   }

   function hasMissingField() {
      const pflichtfelder = config.uebergabeFormular.estateForm.filter((item) => !item.optional);
      let missing = [{ key: "uebergabeDatum" }, ...pflichtfelder].some((item) => !objekt[item.key]);
      return missing;
   }

   // -------------------------------------------------------------------------
   // Render
   // -------------------------------------------------------------------------
   return (
      <div className="d-flex justify-content-center">
         <Container className="py-4 mx-0" style={{ maxWidth: 1180 }}>
            {loading && (
               <div className="d-flex align-items-center gap-2 mb-3 text-muted">
                  <Spinner animation="border" size="sm" /> Objektdaten werden geladen…
               </div>
            )}
            {loadError && (
               <Alert variant="danger">Objektdaten konnten nicht geladen werden: {JSON.stringify(loadError)}</Alert>
            )}

            {/* 01 Objektdaten */}
            <SectionCard step="01" title="Objektdaten" subtitle="Adresse und Eckdaten der Übergabe">
               <Row>
                  {config.uebergabeFormular.estateForm.map((item) => {
                     return (
                        <Col xs={12} md={6}>
                           <Form.Group className="mb-3">
                              <Form.Label>
                                 {item.name}
                                 {!item.optional && <span className="text-danger">*</span>}
                              </Form.Label>
                              <Form.Control
                                 className={item.optional ? "" : objekt[item.key] ? "" : "empty-field"}
                                 name={item.key}
                                 value={objekt?.[item.key]}
                                 onChange={(e) => updateObjekt(e.target.name, e.target.value)}
                              />
                           </Form.Group>
                        </Col>
                     );
                  })}
               </Row>

               <Row>
                  <Col xs={12} md={12}>
                     <div className="col" style={{ position: "relative" }}>
                        <Form.Group controlId={"id_uebergabeDatum"} className="mb-2 mt-2">
                           <Form.Label>Datum und Uhrzeit der Übergabe</Form.Label>

                           <DatePicker
                              selected={isValidDate(objekt.uebergabeDatum) && new Date(objekt.uebergabeDatum)}
                              showTimeSelect={true}
                              timeIntervals={5}
                              onChange={(value) => {
                                 updateObjekt("uebergabeDatum", value);
                              }}
                              className={"bootstrap-datepicker " + (objekt.uebergabeDatum ? "" : " empty-field")}
                              calendarClassName="custom-date-calendar"
                              timeFormat="HH:mm"
                              dateFormat={"dd.MM.yyyy, HH:mm"}
                              // popperPlacement="bottom"
                              locale="de"
                           />
                        </Form.Group>
                        <button
                           title="heute und jetzt als Datum einstellen"
                           className="now-button "
                           onClick={(e) => {
                              e.preventDefault();
                              updateObjekt("uebergabeDatum", new Date());
                           }}
                        >
                           {"˅"}
                        </button>{" "}
                     </div>
                  </Col>
               </Row>
            </SectionCard>

            {/* 02 Beteiligte */}
            <SectionCard step="02" title="Beteiligte" subtitle="E-Mail-Adressen werden für den Versand verwendet">
               <Row className="">
                  {beteiligte.owner.map((beteiligter, index) => {
                     return (
                        <BeteiligterSpalte
                           label="Verkäufer"
                           data={beteiligter}
                           onChange={(field, value) => updateBeteiligte("owner", field, value, index)}
                           emailPlaceholder="name@beispiel.de"
                           removeBeteiligte={() => {
                              removeBeteiligte("owner", index);
                           }}
                        />
                     );
                  })}
                  <AddBeteiligterSpalte label="Verkäufer" addBeteiligte={addBeteiligte} relationType={"owner"} />
               </Row>
               <Row className="">
                  {beteiligte.buyer.map((beteiligter, index) => {
                     return (
                        <BeteiligterSpalte
                           label="Käufer"
                           data={beteiligter}
                           onChange={(field, value) => updateBeteiligte("buyer", field, value, index)}
                           emailPlaceholder="name@beispiel.de"
                           removeBeteiligte={() => {
                              removeBeteiligte("buyer", index);
                           }}
                        />
                     );
                  })}
                  <AddBeteiligterSpalte label="Käufer" addBeteiligte={addBeteiligte} relationType={"buyer"} />
               </Row>
               <Row>
                  <BeteiligterSpalte
                     label="Makler"
                     userlist={usersData?.userList}
                     data={beteiligte.makler[0]}
                     onChange={(field, value) => updateBeteiligte("makler", field, value, 0)}
                     emailPlaceholder="info@baur-immobilien.de"
                  />
               </Row>
            </SectionCard>
            <Row className="mb-4">
               <Col md={12}>
                  <Button
                     variant="secondary"
                     className="w-100"
                     disabled={submitting}
                     onClick={() => {
                        saveData();
                     }}
                  >
                     Daten speichern
                  </Button>
               </Col>
            </Row>
            {/* 03 Zählerstände */}
            <SectionCard step="03" title="Zählerstände" subtitle="Stand ablesen und ein Foto des Zählers aufnehmen">
               {zaehler.map((z) => (
                  <Card key={z.id} className="mb-3 p-3 bg-light">
                     <Row className="align-items-end mb-2">
                        <Col md={4}>
                           <Form.Label>Zählerart</Form.Label>
                           <Form.Select value={z.art} onChange={(e) => updateZaehler(z.id, "art", e.target.value)}>
                              {ZAEHLER_ARTEN.map((a) => (
                                 <option key={a} value={a}>
                                    {a}
                                 </option>
                              ))}
                           </Form.Select>
                           {z.art === "Sonstige" && (
                              <Form.Control
                                 className="mt-2"
                                 placeholder="Bezeichnung der Zählerart"
                                 value={z.artSonstige || ""}
                                 onChange={(e) => updateZaehler(z.id, "artSonstige", e.target.value)}
                              />
                           )}
                        </Col>
                        <Col md={4}>
                           <Form.Label>Zählernummer</Form.Label>
                           <Form.Control
                              placeholder="Nr."
                              value={z.nummer}
                              onChange={(e) => updateZaehler(z.id, "nummer", e.target.value)}
                           />
                        </Col>
                        <Col md={3}>
                           <Form.Label>Zählerstand</Form.Label>
                           <Form.Control
                              placeholder="Stand"
                              value={z.stand}
                              onChange={(e) => updateZaehler(z.id, "stand", e.target.value)}
                           />
                        </Col>
                        <Col md={1} className="text-end">
                           <Button variant="outline-secondary" onClick={() => removeZaehler(z.id)}>
                              ×
                           </Button>
                        </Col>
                     </Row>
                     <PhotoDropzone
                        file={z.foto}
                        onChange={(file) => handleZaehlerFoto(z.id, file)}
                        label="Foto des Zählers aufnehmen"
                     />
                  </Card>
               ))}
               <Button variant="outline-dark" onClick={addZaehler}>
                  + Zähler hinzufügen
               </Button>
            </SectionCard>

            {/* 04 Schlüssel & Übergabegegenstände */}
            <SectionCard
               step="04"
               title="Schlüssel & Übergabegegenstände"
               subtitle="Anzahl der übergebenen Schlüssel und Gegenstände"
            >
               {schluessel.map((s) => (
                  <Row key={s.id} className="mb-2 align-items-center">
                     <Col md={8}>
                        <Form.Control
                           placeholder="Bezeichnung"
                           value={s.bezeichnung}
                           onChange={(e) => updateSchluessel(s.id, "bezeichnung", e.target.value)}
                        />
                     </Col>
                     <Col md={2}>
                        <Form.Control
                           type="number"
                           min={0}
                           value={s.anzahl}
                           onChange={(e) => updateSchluessel(s.id, "anzahl", e.target.value)}
                        />
                     </Col>
                     <Col md={2} className="text-end">
                        <Button variant="outline-secondary" onClick={() => removeSchluessel(s.id)}>
                           ×
                        </Button>
                     </Col>
                  </Row>
               ))}
               <Button variant="outline-dark" onClick={addSchluessel}>
                  + Eintrag hinzufügen
               </Button>
            </SectionCard>

            {/* 05 Zustand, Mängel & Notizen */}
            <SectionCard
               step="05"
               title="Zustand, Mängel & Notizen"
               subtitle="Freitext für Vereinbarungen und Beobachtungen"
            >
               <Form.Group className="mb-3">
                  <Form.Label>Festgestellte Mängel</Form.Label>
                  <Form.Control
                     as="textarea"
                     rows={3}
                     placeholder="z. B. Kratzer Parkett Wohnzimmer, fehlende Abdeckung Steckdose Küche ..."
                     value={maengel}
                     onChange={(e) => setMaengel(e.target.value)}
                  />
               </Form.Group>
               <Form.Group>
                  <Form.Label>Vereinbarungen / sonstige Notizen</Form.Label>
                  <Form.Control
                     as="textarea"
                     rows={3}
                     placeholder="z. B. Restmüll wird bis Freitag entfernt, Nachsendeauftrag eingerichtet ..."
                     value={notizen}
                     onChange={(e) => setNotizen(e.target.value)}
                  />
               </Form.Group>
            </SectionCard>

            {/* 06 Fotos */}
            <SectionCard step="06" title="Fotos" subtitle="Allgemeine Fotos zum Zustand des Objekts">
               <div className="d-flex flex-wrap gap-3">
                  <PhotoDropzone
                     multiple
                     label="Foto aufnehmen"
                     file={null}
                     onChange={(_, fileList) => handleFotosChange(fileList)}
                     compact
                  />
                  {fotos.map((f, i) => (
                     <Badge key={i} bg="secondary" className="align-self-center">
                        {f.name}
                     </Badge>
                  ))}
               </div>
            </SectionCard>

            {/* 07 Unterschriften */}
            <SectionCard step="07" title="Unterschriften" subtitle="">
               {beteiligte.owner.map((beteiligter, index) => {
                  const label =
                     beteiligte.owner.length > 1
                        ? `Verkäufer${beteiligter.name ? " – " + beteiligter.name : ` ${index + 1}`}`
                        : "Verkäufer";
                  return (
                     <SignaturePad
                        key={`owner-${index}`}
                        label={label}
                        value={signatures.owner?.[index] || null}
                        onChange={(v) => updateSignature("owner", index, v)}
                     />
                  );
               })}
               {beteiligte.buyer.map((beteiligter, index) => {
                  const label =
                     beteiligte.buyer.length > 1
                        ? `Käufer${beteiligter.name ? " – " + beteiligter.name : ` ${index + 1}`}`
                        : "Käufer";
                  return (
                     <SignaturePad
                        key={`buyer-${index}`}
                        label={label}
                        value={signatures.buyer?.[index] || null}
                        onChange={(v) => updateSignature("buyer", index, v)}
                     />
                  );
               })}

               <div className="d-flex justify-content-between align-items-baseline mb-2">
                  <div className="text-uppercase fw-semibold small text-warning-emphasis">Makler</div>
                  <div className="text-muted">{beteiligte.makler[0]?.name}</div>
               </div>
               <SignaturePad
                  label=""
                  value={signatures.makler?.[0] || null}
                  onChange={(v) => updateSignature("makler", 0, v)}
               />
            </SectionCard>
            {hasMissingField() && <Alert variant="warning">Objektdaten sind nicht vollständig ausgefüllt!</Alert>}
            <Row className="g-2">
               <Col md={4}>
                  <Button
                     variant="outline-dark"
                     className="w-100"
                     disabled={submitting}
                     onClick={() => {
                        console.log(host + "/images/logo_light.png");

                        handleSubmit("preview", config.colorScheme.signature, host + "/images/logo_light.png");
                     }}
                  >
                     PDF ansehen
                  </Button>
               </Col>
               <Col md={4}>
                  <Button
                     variant="outline-dark"
                     className="w-100"
                     disabled={submitting}
                     onClick={() =>
                        handleSubmit("download", config.colorScheme.signature, host + "/images/logo_light.png")
                     }
                  >
                     PDF herunterladen
                  </Button>
               </Col>
               <Col md={4}>
                  <Button
                     // variant="warning"
                     style={{ background: config.colorScheme.signature, border: "solid 2px #333" }}
                     className="w-100 text-white"
                     disabled={submitting || hasMissingField()}
                     onClick={() => handleSubmit("send", config.colorScheme.signature, host + "/images/logo_light.png")}
                  >
                     {submitting ? <Spinner animation="border" size="sm" /> : "PDF erstellen & per E-Mail senden"}
                  </Button>
               </Col>
            </Row>
         </Container>
      </div>
   );
}

// ---------------------------------------------------------------------------
// Weitere Unterkomponenten
// ---------------------------------------------------------------------------
function SectionCard({ step, title, subtitle, children }) {
   const config = useConfig();
   return (
      <Card className="mb-4 shadow-sm overflow-hidden">
         <Card.Header className="bg-white d-flex align-items-center gap-3 py-3">
            <Badge
               bg="light"
               text="dark"
               className="fs-6 px-3 py-2"
               style={{ border: "solid 3px " + config.colorScheme.signature }}
            >
               {step}
            </Badge>
            <div>
               <div className="fw-bold fs-5">{title}</div>
               {subtitle && <div className="text-muted small">{subtitle}</div>}
            </div>
         </Card.Header>
         <Card.Body>{children}</Card.Body>
      </Card>
   );
}

function BeteiligterSpalte({ label, data, onChange, emailPlaceholder, removeBeteiligte, userlist }) {
   return (
      <Col md={4} className="mb-3">
         <Card className="h-100 p-3 bg-light">
            <div className="text-uppercase fw-semibold small mb-3">{label}</div>
            <Button
               variant="outline-secondary"
               style={{ position: "absolute", right: 0, top: 0, margin: 10 }}
               onClick={() => removeBeteiligte()}
            >
               ×
            </Button>

            {label === "Makler" ? (
               <Form.Group className="mb-3">
                  <Form.Select onChange={(e) => onChange("name", e.target.value)} value={data.name}>
                     <option value=""></option>
                     {userlist &&
                        userlist.map((user, index) => {
                           return (
                              <option key={"selectuser_" + user.Name} value={user.Nachname + " " + user.Vorname}>
                                 {user.Nachname + " " + user.Vorname}
                              </option>
                           );
                        })}
                  </Form.Select>
               </Form.Group>
            ) : (
               <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                     placeholder="Vor- und Nachname"
                     value={data.name}
                     onChange={(e) => onChange("name", e.target.value)}
                  />
               </Form.Group>
            )}
            <Form.Group>
               <Form.Label>E-Mail</Form.Label>
               <Form.Control
                  type="email"
                  placeholder={emailPlaceholder}
                  value={data.email}
                  onChange={(e) => onChange("email", e.target.value)}
               />
            </Form.Group>
         </Card>
      </Col>
   );
}

function AddBeteiligterSpalte({ label, addBeteiligte, relationType }) {
   return (
      <Col md={4} className="mb-3">
         <Card className="h-100 p-3 bg-light">
            <div className="text-uppercase fw-semibold small mb-3">{label}</div>

            <Button
               className="h-100 w-100 "
               variant="outline-dark"
               onClick={() => {
                  addBeteiligte(relationType);
               }}
            >
               hinzufügen
            </Button>
         </Card>
      </Col>
   );
}

function PhotoDropzone({ file, onChange, label, multiple, compact }) {
   const inputRef = useRef(null);

   const handleFiles = (e) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;
      if (multiple) {
         onChange(null, files);
      } else {
         onChange(files[0]);
      }
   };

   return (
      <div
         role="button"
         onClick={() => inputRef.current?.click()}
         className="border border-dashed rounded text-center text-muted d-flex flex-column align-items-center justify-content-center"
         style={{
            borderStyle: "dashed",
            minHeight: compact ? 130 : 150,
            width: compact ? 150 : "100%",
            cursor: "pointer",
            background: "#fff",
         }}
      >
         <input
            ref={inputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple={multiple}
            className="d-none"
            onChange={handleFiles}
         />
         <div className="fw-semibold small mt-1">{file ? file.name : label}</div>
      </div>
   );
}
